import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import type { ReactNode } from 'react'
import { EmployeeChip, employeesQueryOptions, meQueryOptions } from '@/entities/employee'
import { notificationSubscriptionsQueryOptions } from '@/entities/notification'
import { playlistQueryOptions } from '@/entities/track'
import { NotificationSubscriptions } from '@/features/manage-notification-subscriptions'
import { PlaylistManager } from '@/features/manage-playlist'
import { PageHeader } from '@/shared/ui/page-header'

export const Route = createFileRoute('/settings')({
  component: SettingsPage,
  head: () => ({ meta: [{ title: 'Настройки · STIT Task' }] }),
})

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="mt-10 first:mt-0">
      <h2 className="label mb-4">{title}</h2>
      {children}
    </section>
  )
}

function SectionSkeleton({ lines = 1 }: { lines?: number }) {
  return (
    <div className="flex flex-col gap-3">
      {Array.from({ length: lines }, (_, i) => i).map((i) => (
        <div key={i} className="h-10 max-w-sm animate-pulse rounded-control bg-bg-raised" />
      ))}
    </div>
  )
}

function SectionError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex items-center gap-3 text-sm text-text-secondary">
      Не удалось загрузить
      <button
        type="button"
        onClick={onRetry}
        className="text-text-secondary underline transition-colors hover:text-text-primary"
      >
        Повторить
      </button>
    </div>
  )
}

function ProfileSection() {
  const me = useQuery(meQueryOptions)
  return (
    <Section title="Профиль">
      {me.isPending ? (
        <SectionSkeleton lines={2} />
      ) : me.isError ? (
        <SectionError onRetry={() => void me.refetch()} />
      ) : (
        <div className="flex flex-col gap-2">
          <EmployeeChip employee={me.data} size="md" />
          <p className="text-sm text-text-tertiary">
            {me.data.email}
            {me.data.departments.length > 0 ? ` · ${me.data.departments.join(', ')}` : ''} · email
            из хаба, изменить нельзя
          </p>
        </div>
      )}
    </Section>
  )
}

function NotificationsSection() {
  const subscriptions = useQuery(notificationSubscriptionsQueryOptions)
  return (
    <Section title="Уведомления">
      {subscriptions.isPending ? (
        <SectionSkeleton lines={3} />
      ) : subscriptions.isError ? (
        <SectionError onRetry={() => void subscriptions.refetch()} />
      ) : (
        <NotificationSubscriptions subscriptions={subscriptions.data} />
      )}
    </Section>
  )
}

function PlaylistSection({ meId }: { meId: string }) {
  const playlist = useQuery(playlistQueryOptions)
  const employees = useQuery(employeesQueryOptions)
  return (
    <Section title="Плейлист фокуса">
      {playlist.isPending ? (
        <SectionSkeleton lines={2} />
      ) : playlist.isError ? (
        <SectionError onRetry={() => void playlist.refetch()} />
      ) : (
        <PlaylistManager tracks={playlist.data} employees={employees.data ?? []} meId={meId} />
      )}
    </Section>
  )
}

function SettingsPage() {
  const me = useQuery(meQueryOptions)
  return (
    <div className="container-x px-4 py-8 sm:px-6 sm:py-10">
      <PageHeader title="Настройки" eyebrow="Личное" />
      <ProfileSection />
      <NotificationsSection />
      {/* Плейлист правит только админ (PlaylistAdminPolicy) — прячем, 403 — страховка. */}
      {me.data?.isAdmin && <PlaylistSection meId={me.data.id} />}
    </div>
  )
}
